import { defineStore } from "pinia";
import shortid from "shortid";
import { useMjmlStore } from "./mjmlStore";
import { useNotificationsStore } from "./notifications";
import { migrateApiBlock } from "@/utils/migrateApiBlocks";
import {
  SavedBlocksSchema,
  ISavedBlock,
  ISavedBlocks,
} from "@/schemas/savedBlock/savedBlockSchema";
import { SelectPrimaryBlockLanguage } from "@/schemas/template/languageSchema";
import { i18n } from "@/init/i18n-setup";

interface State {
  savedBlocks: ISavedBlocks;
  syncedSectionsEnabled: boolean;
  savedBlocksOpened: boolean;
  SyncedSectionsOpened: boolean;
  editingRowUid?: string;
}

interface AddSavedBlock {
  sectionIndex: number;
  blockIndex: number;
  categoryIndex: number | null;
  emptyBlock?: boolean;
  byClick?: boolean;
}

export const useSavedBlocksStore = defineStore("savedBlocks", {
  state: (): State => ({
    savedBlocks: false,
    syncedSectionsEnabled: false,
    savedBlocksOpened: false,
    SyncedSectionsOpened: false,
  }),

  actions: {
    async addSavedBlock(data: AddSavedBlock) {
      const mjmlStore = useMjmlStore();

      mjmlStore.takeSnapshot();

      let block: ISavedBlock | undefined;
      let props;
      if (
        this.savedBlocks &&
        Array.isArray(this.savedBlocks) &&
        this.savedBlocks.length > 0
      ) {
        if (
          data.categoryIndex !== null &&
          data.categoryIndex !== undefined &&
          data.categoryIndex >= 0
        ) {
          block = this.savedBlocks[data.categoryIndex].children?.find(
            (obj: ISavedBlock) => obj.id == data.blockIndex
          );
        } else {
          block = this.savedBlocks.find((obj) => obj.id == data.blockIndex);
        }
        if (block) {
          props = JSON.parse(JSON.stringify(block.definition));
        } else {
          return false;
        }
      } else {
        if (this.savedBlocks && typeof this.savedBlocks !== "boolean") {
          block = this.savedBlocks[data.blockIndex];
          props = JSON.parse(JSON.stringify(block.definition));
        }
      }

      if (!Array.isArray(props)) {
        props = new Array(props);
      }

      let newIndex = data.sectionIndex;
      if (
        !data.byClick &&
        newIndex !== 0 &&
        newIndex !== mjmlStore.mjml.children[0].children.length
      ) {
        const { newIndex: correntNew } = mjmlStore.getCorrectSectionIndexes({
          oldIndex: 0,
          newIndex: data.sectionIndex,
        });
        newIndex = correntNew;
      }
      const variantOffset = mjmlStore.getSectionIndexVariantOffset(newIndex);

      let sectionIndex = newIndex + variantOffset;

      let primaryTranslation: any = {};
      let primaryLanguage: string | undefined;
      if (mjmlStore.mjml.langs && mjmlStore.mjml.langs.length > 0) {
        primaryLanguage = mjmlStore.mjml.langs.find(
          (lang) => lang.primary
        )?.key;
      }

      if (
        block &&
        block.translation &&
        Object.keys(block.translation).length > 0 &&
        (!mjmlStore.mjml.langs ||
          (mjmlStore.mjml.langs &&
            primaryLanguage &&
            !block.translation[primaryLanguage]))
      ) {
        const langs = Object.keys(block.translation);
        const title = !mjmlStore.mjml.langs
          ? i18n.global.t("layout.blockLanguageSelector.selectMutation")
          : i18n.global.t("layout.blockLanguageSelector.selectPrimaryLanguage");
        const description = !mjmlStore.mjml.langs
          ? i18n.global.t(
              "layout.blockLanguageSelector.selectMutationDescription"
            )
          : i18n.global.t(
              "layout.blockLanguageSelector.selectPrimaryLanguageDescription"
            );
        if (langs.length > 1) {
          const selectedLang = await selectPrimaryLanguage({
            langs: langs,
            title: title,
            description: description,
          });
          if (!selectedLang) return;
          primaryTranslation = block.translation[selectedLang];
        } else {
          primaryTranslation = block.translation[langs[0]];
        }
      }

      props.forEach((section: any, index: number) => {
        for (const property in mjmlStore.defaultSectionProps) {
          // eslint-disable-next-line no-prototype-builtins
          if (mjmlStore.defaultSectionProps.hasOwnProperty(property)) {
            // @ts-expect-error TODO: fix noImplicitAny error here
            section[property] = mjmlStore.defaultSectionProps[property];
          }
        }
        for (const col of section.children) {
          col.uid = shortid.generate();
          col.children = col.children.map((bl: any) => migrateApiBlock(bl));
          for (const bl of col.children) {
            const previousUid = bl.uid;
            bl["uid"] = shortid.generate();
            const blockTranslation = primaryTranslation[previousUid];
            if (!mjmlStore.mjml.langs && blockTranslation) {
              if ("content" in blockTranslation && "content" in bl) {
                bl.content = blockTranslation.content;
              }
              if ("attributes" in blockTranslation) {
                for (const attribute in blockTranslation.attributes) {
                  bl.attributes[attribute] =
                    blockTranslation.attributes[attribute];
                }
              }
              if ("children" in blockTranslation && "children" in bl) {
                for (let i = 0; i < bl.children.length; i++) {
                  const child = bl.children[i];
                  const childTranslation = blockTranslation.children?.[i];
                  if (
                    childTranslation &&
                    "attributes" in childTranslation &&
                    "attributes" in child
                  ) {
                    for (const attribute in childTranslation.attributes) {
                      child.attributes[attribute] =
                        childTranslation.attributes[attribute];
                    }
                  }
                }
              }
            }
            if (mjmlStore.mjml.langs) {
              mjmlStore.addSpecialBlockTranslations({
                block: bl,
                previousUid: previousUid,
                translations: block?.translation,
                primaryTranslation: primaryTranslation,
              });
            }
          }
        }

        section["uid"] = shortid.generate();
        delete section.sectionIndex;

        //if adding savedblock to empty structure, we need to replace the structure
        let removeElements = 0;
        if (data.emptyBlock && index === 0) {
          removeElements = 1;
        }

        mjmlStore.mjml.children[0].children.splice(
          sectionIndex,
          removeElements,
          section
        );
        sectionIndex++;
      });
      mjmlStore.edittedWithoutSave = true;
    },
    setSavedBlocks(savedBlocks: ISavedBlocks) {
      const { data, error, success } = SavedBlocksSchema.safeParse(savedBlocks);
      if (!success) {
        console.error("Error loading saved blocks", error.issues);
        this.savedBlocks = false;
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          text: "Error loading saved blocks",
          type: "error",
        });
        return;
      }
      this.savedBlocks = data;
    },
  },
});

async function selectPrimaryLanguage(
  props: SelectPrimaryBlockLanguage
): Promise<string | null> {
  return new Promise((resolve) => {
    // Předáme modalu pole jazyků a callback
    window.$event.emit("selectPrimaryLanguage", {
      langs: props.langs,
      title: props.title,
      description: props.description,
      resolve, // ten zavolá modal, až bude mít výběr
    });
  });
}
